<template>
	<div>
		<h1 v-if="selectedHypermarket" class="typo-h1 text-heading-primary mb-2">
			Akční leták
		</h1>

		<p v-if="leaflet" class="mb-4 typo-body-m">
			Pro hypermarket Globus
			<NuxtLink
				v-if="branchSlug && branchName"
				:to="{name: 'branch', params: {branch: branchSlug}}"
				class="text-brand-globus-green underline hover:no-underline"
			>
				{{ branchName }}
			</NuxtLink>.
			<template v-if="leafletValidityText">
				{{ leafletValidityText }}
			</template>
		</p>

		<span
			v-if="validityStartDateAlert"
			class="mb-4 text-body-important font-semibold flex items-center gap-2 typo-body-m"
		>
			<NuxtIcon name="custom/alert-round" class="text-lg text-icon-primary" filled />
			{{ validityStartDateAlert }}
		</span>
		<span
			v-else-if="validityEndDateAlert"
			class="mb-4 text-brand-globus-green font-bold flex items-center gap-2 typo-body-m"
		>
			<NuxtIcon name="info" class="text-lg text-icon-primary" filled />
			{{ validityEndDateAlert }}
		</span>

		<div class="flex gap-2 flex-wrap md:flex-col md:justify-start md:items-start">
			<NuxtLink
				:to="{name: 'branch-letaky', params: {branch: branchSlug}}"
				class="btn btn-sm md:btn-lg btn-secondary shadow-sm justify-center"
			>
				Všechny letáky a katalogy
				<NuxtIcon name="custom/leaflet" filled class="text-xl" />
			</NuxtLink>

			<NuxtLink
				v-if="leaflet?.storeDocuments?.[0]?.pdfAsset"
				:to="leaflet?.storeDocuments?.[0]?.pdfAsset"
				class="btn btn-sm md:btn-lg btn-secondary shadow-sm justify-center"
				target="_blank"
				download
				@click="trackDownload"
			>
				Stáhnout v PDF
				<NuxtIcon name="download" filled class="text-lg" />
			</NuxtLink>
		</div>
	</div>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {getLeafletGTMPageType} from '~/utils/leaflets';
	import {leafletValidityStartDate, leafletValidityEndDate} from '~/utils/leafletValidity';
	import {formatPragueDate} from '~/utils/date';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import type {Gsoa_ActionOffers_ActionOffer as ActionOffer} from '~/openapi';

	type Props = {
		leaflet: ActionOffer | null;
	};

	const props = defineProps<Props>();
	const leaflet = toRef(props, 'leaflet');

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketWithFallbackInstance} = storeToRefs(hypermarketsStore);

	const branchName = computed(() => selectedHypermarketWithFallbackInstance.value?.content?.name);
	const branchSlug = computed(() => selectedHypermarketWithFallbackInstance.value?.slug);

	const gtmPageType = computed(() => getLeafletGTMPageType(leaflet.value));
	const {trackDownload} = useLeafletTracking(leaflet, gtmPageType);

	const leafletValidityText = computed(() => {
		if (!props.leaflet?.validFrom || !props.leaflet?.validTo) {
			return null;
		}

		const from = formatPragueDate(props.leaflet.validFrom, 'd. M.');
		const to = formatPragueDate(props.leaflet.validTo, 'd. M. yyyy');

		return `Platný od ${from} do ${to}`;
	});

	const validityStartDateAlert = computed(() =>
		leaflet.value?.validFrom ? leafletValidityStartDate(leaflet.value.validFrom) : null,
	);

	const validityEndDateAlert = computed(() =>
		leaflet.value?.validTo ? leafletValidityEndDate(leaflet.value.validTo) : null,
	);
</script>
